import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../../styles/Blog/Blog.css';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [blogData, setBlogData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogData = async () => {
      try {
        const response = await fetch('/blog-data.json');
        if (!response.ok) {
          throw new Error('Failed to fetch blog data');
        }
        const data = await response.json();
        setBlogData(data);
      } catch (err) {
        setError(err.message);
        console.error('Error loading blog data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogData();
  }, []);

  // Function to get image source with fallback
  const getImageSrc = (imagePath) => {
    return imagePath || '/blog-images/fallback.jpg';
  };

  // Function to dynamically calculate categories and counts
  const calculateCategories = (posts) => {
    if (!posts) return [];
    
    const categoryCounts = {};
    posts.forEach(post => {
      categoryCounts[post.category] = (categoryCounts[post.category] || 0) + 1;
    });

    const categories = [
      { id: 'all', name: 'All Posts', count: posts.length }
    ];

    // Add dynamic categories
    Object.entries(categoryCounts).forEach(([categoryId, count]) => {
      categories.push({
        id: categoryId,
        name: categoryId.charAt(0).toUpperCase() + categoryId.slice(1),
        count: count
      });
    });

    return categories;
  };

  if (loading) {
    return (
      <div className="blog-page" style={{ marginTop: '80px', padding: '3rem 0' }}>
        <div className="container">
          <div className="loading" style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="loading-spinner"></div>
            <h3>Loading Construction Guides</h3>
            <p>Fetching the latest construction insights for Pakistan...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-page" style={{ marginTop: '80px', padding: '3rem 0' }}>
        <div className="container">
          <div className="error" style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="error-icon">⚠️</div>
            <h3>Unable to Load Blog</h3>
            <p>{error}</p>
            <button onClick={() => window.location.reload()} className="retry-btn">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!blogData || !blogData.posts || blogData.posts.length === 0) {
    return (
      <div className="blog-page" style={{ marginTop: '80px', padding: '3rem 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <div className="empty-state">📝</div>
            <h3>No Construction Guides Available</h3>
            <p>We're preparing valuable construction insights for Pakistan. Check back soon!</p>
          </div>
        </div>
      </div>
    );
  }

  const { posts } = blogData;
  const categories = calculateCategories(posts);

  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  return (
    <motion.div 
      className="blog-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container">
        {/* Simplified Professional Header */}
        <div className="blog-header">
          <motion.div
            className="header-content"
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Construction Insights</h1>
            {/* <p>We provide expert construction guides and up-to-date cost analysis for Pakistan, helping you estimate building material prices with confidence. From house construction planning to budgeting, we deliver accurate insights for every step of your project. Build smarter, save money, and avoid unexpected costs.</p> */}
          </motion.div>
        </div>

        {/* Categories Filter */}
        <motion.div 
          className="blog-categories"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="categories-scroll">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <span className="btn-text">{category.name}</span>
                <span className="count-badge">{category.count}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Results Info */}
        <motion.div 
          className="results-info"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <p>
            Showing <strong>{filteredPosts.length}</strong> 
            {filteredPosts.length === 1 ? ' construction guide' : ' construction guides'}
            {selectedCategory !== 'all' && ` in ${categories.find(c => c.id === selectedCategory)?.name}`}
          </p>
        </motion.div>

        {/* Blog Grid */}
        <motion.div 
          className="blog-grid"
          layout
        >
          {filteredPosts.map((post, index) => (
            <motion.article
              key={post.id}
              className="blog-card"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
            >
              <div className="card-image-container">
                <img 
                  src={getImageSrc(post.image)} 
                  alt={post.title}
                  className="blog-image"
                  onError={(e) => {
                    e.target.src = '/blog-images/fallback.jpg';
                  }}
                />
                <div className="image-overlay">
                  <span className={`category-tag ${post.category}`}>{post.category}</span>
                </div>
                <div className="read-time-badge">{post.readTime}</div>
              </div>
              
              <div className="blog-content">
                <div className="blog-meta">
                  <span className="blog-date">
                    <i className="calendar-icon">📅</i>
                    {post.date}
                  </span>
                </div>
                
                <h3 className="blog-title">{post.title}</h3>
                <p className="blog-excerpt">{post.excerpt}</p>
                
                <div className="blog-footer">
                  <div className="post-tags">
                    {post.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="tag-pill">{tag}</span>
                    ))}
                  </div>
                  <Link to={`/blog/${post.id}`} className="read-more-btn">
                    Read Guide
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Link>
                </div>
              </div>
            </motion.article>
          ))}
        </motion.div>

        {/* Professional Newsletter */}
        <motion.section 
          className="professional-newsletter"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="newsletter-content">
            <div className="newsletter-text">
              <h3>Stay Updated with Construction Insights</h3>
              <p>Get the latest material prices, regulatory updates, and professional construction guides delivered to your inbox.</p>
            </div>
            <div className="newsletter-form">
              <div className="form-group">
                <input type="email" placeholder="Enter your email" className="professional-input" />
                <select className="professional-select">
                  <option value="">Select City</option>
                  <option value="lahore">Lahore</option>
                  <option value="karachi">Karachi</option>
                  <option value="islamabad">Islamabad</option>
                  <option value="rawalpindi">Rawalpindi</option>
                  <option value="faisalabad">Faisalabad</option>
                  <option value="multan">Multan</option>
                </select>
              </div>
              <button type="submit" className="professional-submit">
                <span>Subscribe</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </motion.section>
      </div>
    </motion.div>
  );
};

export default Blog;